import Chip from '@mui/material/Chip'
import Grid from '@mui/material/Grid'
import { useTheme } from '@mui/material/styles'
import Tooltip from '@mui/material/Tooltip'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { useGetTagsMetadataQuery } from 'api/tags/getTagsMetadata'
import EditTagsPopup from 'components/tags/EditTagsPopup'
import Tag from 'components/tags/Tag'
import { getEnabledTypes, getTagsGroupInfo, addParentIdToTagGroupTypes } from 'data/tags/tagsDataMappingSelector'
import { getActiveTags } from 'data/tags/tagsMetadataSelector'
import { TAG_TYPE_ACCOUNT, TAG_TYPE_LOCATION, TAG_TYPE_SERVICE } from 'slices/tags/tagsSlice'
import T from 'T'

import type { ChipProps } from '@mui/material/Chip'
import type { GridProps } from '@mui/material/Grid'
import type { TagResponse } from 'types/entities/shared'
import type { SelectedTagsByType, SelectedTagsGroup, TagGroupType, TagMetadata } from 'types/modules/tags'

type TagWithGroups = SelectedTagsGroup['tags'][number] & { enabledGroups?: TagGroupType[] }

export type InteractiveTagsData = {
  id?: string | null
  accountId?: string | null
  accountName?: string | null
  locationId?: string | null
  configuredServiceId?: string | null
  name?: string | null
  tagDetails?: Partial<TagResponse> | null
}

type Props = {
  accountId?: string | null
  data?: InteractiveTagsData | null
  disableAccount?: boolean
  disableLocation?: boolean
  disableService?: boolean
  disableTooltip?: boolean
  limit?: number
  tagTypeOrder?: readonly TagGroupType[]
  onOpen?: (parentId?: string | null) => void
  onSave?: (data: InteractiveTagsData | null | undefined, closePopup: () => void) => void
  onClose?: () => void
  size?: ChipProps['size']
  tagsFlexWrap?: GridProps['wrap']
  TagGridItemProps?: GridProps
  RemainingTagsGridItemProps?: GridProps
  GridContainerProps?: GridProps
  tagMaxWidth?: number | string
  isViewOnly?: boolean
  stopPropagation?: boolean
  hideInactiveTags?: boolean
  includeTagNameInTooltip?: boolean
  onConfirm?: React.ComponentProps<typeof EditTagsPopup>['onConfirm']
  SaveButtonProps?: React.ComponentProps<typeof EditTagsPopup>['SaveButtonProps']
}

const TAG_TYPES_LABELS: Record<string, string> = {
  [TAG_TYPE_ACCOUNT]: T.ACCOUNT_TAG,
  [TAG_TYPE_LOCATION]: T.LOCATION_TAG,
  [TAG_TYPE_SERVICE]: T.SERVICE_TAG,
}

// `includeTagName` prefixes the full tag name for surfaces that truncate chips.
const getTooltipText = ({ tagName, enabledGroups = [] }: TagWithGroups, includeTagName = false) => {
  const groups = enabledGroups.map(accountType => TAG_TYPES_LABELS[accountType]).join(', ')
  return includeTagName ? [tagName, groups].filter(Boolean).join(' — ') : groups
}

// Types missing from `order` rank after the listed ones, in their default order.
const getTagTypeRank = (type: string, order: readonly TagGroupType[]) => {
  const rank = order.indexOf(type as TagGroupType)
  return rank === -1 ? order.length : rank
}

const InteractiveTagsList: React.FC<Props> = ({
  accountId,
  data,
  disableAccount = false,
  disableLocation = false,
  disableService = false,
  disableTooltip = false,
  limit = 0,
  tagTypeOrder,
  onOpen,
  onSave,
  onClose,
  size = 'small',
  tagsFlexWrap = 'wrap',
  TagGridItemProps = {},
  RemainingTagsGridItemProps = {},
  GridContainerProps = { direction: 'row', justifyContent: 'flex-start', alignItems: 'center', spacing: 0.5 },
  tagMaxWidth = 200,
  isViewOnly = false,
  stopPropagation = true,
  hideInactiveTags = true,
  includeTagNameInTooltip = false,
  onConfirm,
  SaveButtonProps,
}) => {
  const theme = useTheme()
  const container = useRef<HTMLDivElement>(null)
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false)
  const enabledTagTypes = getEnabledTypes({ disableAccount, disableLocation, disableService })
  const orderedTagTypes = tagTypeOrder
    ? [...enabledTagTypes].sort((a, b) => getTagTypeRank(a, tagTypeOrder) - getTagTypeRank(b, tagTypeOrder))
    : enabledTagTypes

  // CS-2624: We need the tag metadata to know which assigned tags are still
  // active. The assigned tag objects on account/customer responses do not
  // include the `active` flag, so we cross-reference against the metadata.
  // RTK Query dedupes the request when many list rows mount the component.
  useGetTagsMetadataQuery(undefined, { skip: !hideInactiveTags })
  const allActiveTags: TagMetadata[] = useSelector(getActiveTags)
  const activeTagIdSet = useMemo(() => new Set((allActiveTags || []).map(({ id }) => id)), [allActiveTags])

  // raw saved tag groups passed to the editor (must include inactive ones so the
  // popup's preserved-inactive logic can keep them on save).
  const selectedTagsByType = data?.tagDetails ?? {}
  const enabledSavedTags = Object.fromEntries(
    orderedTagTypes.filter(type => type in selectedTagsByType).map(type => [type, selectedTagsByType[type as TagGroupType]])
  )
  const filteredSavedTags: SelectedTagsByType = addParentIdToTagGroupTypes(data ?? undefined, enabledSavedTags)

  // CS-2624: For DISPLAY only, drop any assigned tag whose id is not in the
  // active metadata. We keep the unfiltered set in `filteredSavedTags` for the
  // popup so the destructive-save fix can still preserve them.
  const visibleSavedTagsByType = hideInactiveTags
    ? Object.entries(filteredSavedTags).reduce<SelectedTagsByType>((acc, [type, group]) => {
        acc[type as TagGroupType] = {
          parentId: group?.parentId ?? null,
          tags: (group?.tags || []).filter(tag => activeTagIdSet.has(tag?.id)),
        }
        return acc
      }, {})
    : filteredSavedTags

  // add enabledGroups prop to each tags object indicating the assigned tag groups ['forAccount' and or 'forLocation' and or 'forService']
  const allSelectedTags = Object.entries(visibleSavedTagsByType).flatMap(([type, group]) =>
    (group?.tags ?? []).map(tag => ({
      ...tag,
      enabledGroups: type,
    }))
  )
  const allSelectedTagsWithTagsGroupInfo: TagWithGroups[] = getTagsGroupInfo(allSelectedTags)

  // determine if we need to limit teh amount of tags displayed
  const shouldLimitTagsCount = limit > 0 && limit < allSelectedTagsWithTagsGroupInfo.length
  const normalizedTags = shouldLimitTagsCount ? allSelectedTagsWithTagsGroupInfo.slice(0, limit) : allSelectedTagsWithTagsGroupInfo
  const remainingTags = shouldLimitTagsCount ? allSelectedTagsWithTagsGroupInfo.slice(limit) : []
  const remainingTagsCount = shouldLimitTagsCount ? Math.floor(allSelectedTagsWithTagsGroupInfo.length - normalizedTags.length) : 0
  const remainingTagsTooltip = disableTooltip ? '' : remainingTags.map(tag => getTooltipText(tag, true)).join('\n')
  const showAddMoreTagsChip = (!shouldLimitTagsCount || remainingTagsCount === 0) && normalizedTags.length > 0

  const onEditTagsOpen = useCallback(
    (e: React.SyntheticEvent) => {
      if (stopPropagation) {
        e.preventDefault()
        e.stopPropagation()
      }

      if (isViewOnly) {
        return
      }
      // the parent entity id [RouteId, Service, Location]
      const parentId = data?.id

      setIsEditPopupOpen(true)
      onOpen?.(parentId)
    },
    [stopPropagation, isViewOnly, data, onOpen]
  )

  const onEditTagsClose = useCallback(() => {
    setIsEditPopupOpen(false)
    onClose?.()
  }, [onClose])

  const onSaveTags = useCallback(() => {
    onSave?.(data, () => setIsEditPopupOpen(false))
  }, [onSave, data])

  return (
    <>
      <Grid ref={container} container wrap={tagsFlexWrap} {...GridContainerProps}>
        {!isViewOnly && !allSelectedTags.length && (
          <Grid>
            <Chip
              label={T.ADD_A_TAG_LABEL}
              size="small"
              sx={{
                borderStyle: 'dashed',
                borderWidth: 1.5,
                '& .MuiChip-label': { color: theme.palette.text.secondary, textWeight: 'bold', overflow: 'inherit' },
              }}
              variant="outlined"
              onClick={onEditTagsOpen}
            />
          </Grid>
        )}
        {normalizedTags.map((tag, index) => {
          if (!tag || !tag.tagName) {
            return false
          }
          const tagKey = [data?.id, data?.accountId, data?.locationId, data?.configuredServiceId, tag.tagName, index, 'TagChip']
            .filter(Boolean)
            .join('-')
          return (
            <Grid {...TagGridItemProps} key={tagKey}>
              <Tooltip title={disableTooltip ? '' : getTooltipText(tag, includeTagNameInTooltip)}>
                <div>
                  <Tag
                    aria-label={disableTooltip ? undefined : getTooltipText(tag, true)}
                    clickable={!isViewOnly}
                    maxWidth={tagMaxWidth}
                    size={size}
                    tabIndex={isViewOnly ? 0 : undefined}
                    tag={tag}
                    onClick={onEditTagsOpen}
                  />
                </div>
              </Tooltip>
            </Grid>
          )
        })}
        {shouldLimitTagsCount && (
          <Grid {...RemainingTagsGridItemProps}>
            {remainingTagsCount && (
              <Tooltip slotProps={{ tooltip: { sx: { whiteSpace: 'pre-line' } } }} title={remainingTagsTooltip}>
                <Chip
                  className="add-more-tags-chip"
                  clickable={!isViewOnly}
                  label={`+${remainingTagsCount}`}
                  size="small"
                  sx={{
                    borderRadius: 1,
                    '& .MuiChip-label': { overflow: 'inherit' },
                  }}
                  tabIndex={isViewOnly ? 0 : undefined}
                  onClick={onEditTagsOpen}
                />
              </Tooltip>
            )}
          </Grid>
        )}
        {!isViewOnly && showAddMoreTagsChip && (
          <Grid>
            <Chip
              label="+"
              size="small"
              sx={{
                borderStyle: 'dashed',
                borderWidth: 1.5,
                '& .MuiChip-label': { color: theme.palette.text.secondary, textWeight: 'bold', overflow: 'inherit' },
              }}
              variant="outlined"
              onClick={onEditTagsOpen}
            />
          </Grid>
        )}
      </Grid>
      {isEditPopupOpen && (
        <EditTagsPopup
          accountId={accountId ?? ''}
          defaultSelectedTags={filteredSavedTags}
          disableAccount={disableAccount}
          disableLocation={disableLocation}
          disableService={disableService}
          name={data?.name || data?.accountName || ''}
          open={isEditPopupOpen}
          SaveButtonProps={SaveButtonProps}
          onClose={onEditTagsClose}
          onConfirm={onConfirm}
          onSave={onSaveTags}
        />
      )}
    </>
  )
}

export default InteractiveTagsList
